<script lang="ts">
	import Icon from "@components/common/Icon.svelte";
	import { onMount } from "svelte";
	import type { HTMLDialogAttributes } from "svelte/elements";
	import cross from "@core/assets/icons/cross.svg?raw";
	import { lockScroll } from "../../state/lockScroll.svelte.js";

	let {
		show = $bindable(false),
		/* eslint-disable prefer-const */
		...rest
		/* eslint-enable prefer-const */
	}: HTMLDialogAttributes & { show: boolean } = $props();

	let dialog = $state<HTMLDialogElement | null>(null);

	$effect(() => {
		if (!dialog) {
			return;
		}

		if (show && !dialog.open) {
			dialog.showModal();
		}
		if (!show && dialog.open) {
			dialog.close();
		}
	});

	$effect(() => {
		if (show && !lockScroll.lock) {
			lockScroll.lock = true;
		}
	});
	$effect(() => {
		if (!show) {
			lockScroll.lock = false;
		}
	});

	function closeOnBackdrop(event: MouseEvent): void {
		if (!event.target) {
			return;
		}

		if (!(event.target instanceof Node)) {
			return;
		}

		if (event.target.nodeName === "DIALOG") {
			show = false;
		}
	}

	// Not using on:click because svelte complains about accessibility (non-interactable elements with a mouse/keyboard event listener).
	onMount(() => {
		dialog?.addEventListener("click", closeOnBackdrop);
		return (): void => {
			dialog?.removeEventListener("click", closeOnBackdrop);
		};
	});
</script>

<dialog
	bind:this={dialog}
	{...rest}
	class={[
		"backdrop:bg-black/60 backdrop:backdrop-blur-md bg-transparent p-4 lg:min-w-96 max-w-full mx-auto animate-in fade-in duration-500",
		rest.class,
	]}
	onclose={(event: Event & { currentTarget: EventTarget & HTMLDialogElement }): void => {
		show = false;
		rest.onclose?.(event);
	}}
>
	<div
		class="relative bg-white/95 backdrop-blur-xl rounded-2xl border border-white/20 animate-in zoom-in-95 slide-in-from-bottom-4 fade-in duration-500 max-h-[90vh] overflow-hidden"
	>
		<div
			class="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-zms-grey/5 pointer-events-none rounded-2xl"
		></div>

		<div class="absolute top-0 right-0 z-20 p-3 sm:p-4">
			<button
				aria-label="Zavřít"
				class="text-zms-optical-black/50 hover:text-zms-blue transition-all duration-300 p-2.5 rounded-full bg-transparent backdrop-blur-sm group border border-white/30"
				onclick={(): void => {
					show = false;
				}}
			>
				<Icon icon={cross} class="w-3.5 sm:w-4 group-hover:scale-110 transition-transform duration-200" square />
			</button>
		</div>

		<div
			class="relative z-10 px-6 sm:px-8 md:px-12 lg:px-16 py-8 sm:py-10 md:py-12 lg:py-14 overflow-y-auto max-h-[90vh]"
		>
			<div
				class="absolute inset-6 bg-gradient-to-br from-zms-blue/[0.02] via-transparent to-zms-grey/[0.03] rounded-xl pointer-events-none"
			></div>

			<div
				class="relative max-w-none text-zms-paragraph leading-relaxed [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&>h1]:text-zms-blue [&>h2]:text-zms-blue [&>h3]:text-zms-optical-black flex flex-col gap-3 [&>p]:text-zms-paragraph [&>p]:leading-7 [&>ul]:space-y-2 [&>ol]:space-y-2"
			>
				{@render rest.children?.()}
			</div>
		</div>

		<div
			class="absolute bottom-0 left-1/2 -translate-x-1/2 w-4/5 h-px bg-gradient-to-r from-transparent via-zms-grey/20 to-transparent"
		></div>
	</div>
</dialog>
